/**
 * Promo Service - API calls for Promo Code Management
 * 
 * Handles all promo-related API operations:
 * - List promos with filtering and pagination
 * - Create new promos
 * - Update existing promos
 * - Update promo status
 * - Delete promos
 */

import { PROMOS_ENDPOINTS } from './endpoints';
import secureApi from '../../utils/secureApi';
import {
  Promo,
  PromoListResponse,
  PromoListParams,
  PromoFormData,
  PromoStatus,
  BenefitType,
  BenefitLevel,
  Benefit,
  DiscountMode,
  EntityTargetingMode,
  PromoVisibilityType,
} from './types/promoTypes';

const cleanPayload = (obj: any, preserveEmptyArrays: boolean = false): any => {
  if (obj === null || obj === undefined) {
    return undefined;
  }

  if (typeof obj === 'string') {
    return obj;
  }

  if (Array.isArray(obj)) {
    const cleaned = obj
      .map((item) => cleanPayload(item, false))
      .filter((item) => item !== undefined && item !== null);

    if (preserveEmptyArrays || cleaned.length > 0) {
      return cleaned;
    }
    return undefined;
  }

  if (typeof obj === 'object') {
    const cleaned: any = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        const shouldPreserveEmpty =
          (key === 'entityIds' || key === 'subEntityIds') && Array.isArray(obj[key]);
        const cleanedValue = cleanPayload(obj[key], shouldPreserveEmpty);
        if (cleanedValue !== undefined && cleanedValue !== null) {
          cleaned[key] = cleanedValue;
        }
      }
    }
    return Object.keys(cleaned).length > 0 ? cleaned : undefined;
  }

  return obj;
};

const buildPromoPayload = (
  promoData: PromoFormData,
  options: { status?: PromoStatus } = {},
): any => {
  if (!promoData.benefit) {
    throw new Error('Benefit is required');
  }

  const benefitType = promoData.benefit.type as string;
  const shouldMapToFlat = benefitType === 'FIXED_DISCOUNT';

  const benefit: Benefit = {
    ...promoData.benefit,
    type: shouldMapToFlat
      ? BenefitType.FLAT_DISCOUNT
      : promoData.benefit.type,
    applicableCatalogueCategory:
      promoData.benefit.level === BenefitLevel.ITEM &&
      promoData.benefit.applicableCatalogueCategory
        ? promoData.benefit.applicableCatalogueCategory
        : undefined,
  };

  const conditions: any = {
    fullPaymentRequired: true,
  };
  if (promoData.conditions?.minOrderAmount !== undefined) {
    conditions.minOrderAmount = promoData.conditions.minOrderAmount;
  }
  if (promoData.conditions?.minQuantity !== undefined) {
    conditions.minQuantity = promoData.conditions.minQuantity;
  }
  if (promoData.conditions?.minDurationMonths !== undefined) {
    conditions.minDurationMonths = promoData.conditions.minDurationMonths;
  }

  const stackingRules: any = {
    allowWithReferral: false,
    allowWithOtherPromos: false,
    exclusive: promoData.stackingRules?.exclusive || false,
  };
  if (promoData.stackingRules?.maxTotalDiscountPercent !== undefined) {
    stackingRules.maxTotalDiscountPercent = promoData.stackingRules.maxTotalDiscountPercent;
  }

  const payload: any = {
    name: promoData.name,
    code: promoData.code,
    listingTitle: promoData.listingTitle,
    appliedTitle: promoData.appliedTitle,
    domain: promoData.domain,
    transactionType: promoData.transactionType,
    discountMode: promoData.discountMode || DiscountMode.CODE_BASED,
    eligibility: promoData.eligibility,
    benefit,
    visibility: {
      type: promoData.visibility?.type || PromoVisibilityType.PUBLIC,
    },
  };

  if (options.status) {
    payload.status = options.status;
  }

  if (promoData.campaignId) {
    payload.campaignId = promoData.campaignId;
  }

  if (promoData.scope) {
    payload.scope = {};
    if (
      promoData.scope.entityTargeting &&
      (promoData.scope.entityTargeting.mode === EntityTargetingMode.INCLUDE ||
        promoData.scope.entityTargeting.mode === EntityTargetingMode.EXCLUDE)
    ) {
      const entityTargeting: any = {
        mode: promoData.scope.entityTargeting.mode,
      };
      entityTargeting.entityIds = promoData.scope.entityTargeting.entityIds || [];
      entityTargeting.subEntityIds = promoData.scope.entityTargeting.subEntityIds || [];
      payload.scope.entityTargeting = entityTargeting;
    }
    if (promoData.scope.filters && promoData.scope.filters.length > 0) {
      payload.scope.filters = promoData.scope.filters;
    }
    if (Object.keys(payload.scope).length === 0) {
      delete payload.scope;
    }
  }

  if (promoData.usageLimits) {
    const usageLimits: any = {};
    if (promoData.usageLimits.totalLimit !== undefined) {
      usageLimits.totalLimit = promoData.usageLimits.totalLimit;
    }
    if (promoData.usageLimits.perUserLimit !== undefined) {
      usageLimits.perUserLimit = promoData.usageLimits.perUserLimit;
    }
    if (Object.keys(usageLimits).length > 0) {
      payload.usageLimits = usageLimits;
    }
  }

  payload.conditions = conditions;
  payload.stackingRules = stackingRules;

  if (promoData.funding) {
    payload.funding = {
      fundedBy: promoData.funding.fundedBy,
      sharePercentage:
        promoData.funding.sharePercentage !== undefined
          ? promoData.funding.sharePercentage
          : 100,
    };
  } else {
    payload.funding = {
      fundedBy: 'PLATFORM',
      sharePercentage: 100,
    };
  }

  if (promoData.validity && promoData.validity.startAt && promoData.validity.endAt) {
    const validity: any = {
      startAt: promoData.validity.startAt,
      endAt: promoData.validity.endAt,
    };
    if (promoData.validity.scheduleActivation) {
      validity.scheduleActivation = promoData.validity.scheduleActivation;
    }
    if (promoData.validity.scheduleDate) {
      validity.scheduleDate = promoData.validity.scheduleDate;
    }
    payload.validity = validity;
  }

  return cleanPayload(payload);
};

/**
 * Transform API response to Promo interface
 */
const transformPromoResponse = (item: any): Promo => {
  // Map FLAT_DISCOUNT to FIXED_DISCOUNT for consistency
  const benefit = item.benefit ? {
    ...item.benefit,
    type: item.benefit.type === 'FLAT_DISCOUNT' ? 'FIXED_DISCOUNT' : item.benefit.type,
  } : item.benefit;
  
  return {
    id: item._id || item.id,
    name: item.name,
    code: item.code,
    listingTitle: item.listingTitle,
    appliedTitle: item.appliedTitle,
    campaignId: item.campaignId,
    campaignName: item.campaignName,
    domain: item.domain,
    transactionType: item.transactionType,
    status: item.status,
    discountMode: item.discountMode,
    eligibility: item.eligibility,
    benefit: benefit,
    scope: item.scope,
    visibility: item.visibility
      ? {
          // API returns { type: "ASSIGNED" | "PUBLIC" }
          type: item.visibility.type,
        }
      : undefined,
    usageLimits: item.usageLimits,
    stackingRules: item.stackingRules,
    conditions: item.conditions,
    funding: item.funding,
    validity: item.validity,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    totalUsage: item.totalUsages || item.totalUsage || 0,
    perUserUsage: item.perUserUsage || 0,
    revenueImpact: item.revenueImpact || 0,
  };
};

/**
 * Get all promos with filtering and pagination
 */
export const getPromos = async (params: PromoListParams = {}): Promise<PromoListResponse> => {
  try {
    const queryParams = new URLSearchParams();
    
    // Add query parameters
    if (params.pageNum) queryParams.append('pageNum', params.pageNum.toString());
    if (params.pageSize) queryParams.append('pageSize', params.pageSize.toString());
    if (params.status) queryParams.append('status', params.status);
    if (params.domain) queryParams.append('domain', params.domain);
    if (params.transactionType) queryParams.append('transactionType', params.transactionType);
    if (params.campaignId) queryParams.append('campaignId', params.campaignId);
    if (params.search) queryParams.append('search', params.search);
    if (params.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params.startAt) queryParams.append('startAt', params.startAt);
    if (params.endAt) queryParams.append('endAt', params.endAt);
    
    const endpoint = `${PROMOS_ENDPOINTS.PROMOS.LIST}?${queryParams.toString()}`;
    const response = await secureApi.get(endpoint);
    
    // Transform API response
    const data = response.data?.data || response.data;
    
    if (data.paginatedResults) {
      // Handle paginated response
      const promos = data.paginatedResults.map(transformPromoResponse);
      const totalCount = data.totalCount || 0;
      const currentPage = params.pageNum || 1;
      const pageSize = params.pageSize || 10;
      const totalPages = Math.ceil(totalCount / pageSize);
      
      return {
        promos,
        totalCount,
        currentPage,
        totalPages,
      };
    } else if (Array.isArray(data)) {
      // Handle array response
      const promos = data.map(transformPromoResponse);
      return {
        promos,
        totalCount: promos.length,
        currentPage: 1,
        totalPages: 1,
      };
    } else {
      // Handle single item or unexpected format
      return {
        promos: [],
        totalCount: 0,
        currentPage: 1,
        totalPages: 0,
      };
    }
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || 
                        error?.response?.data?.error || 
                        error?.message || 
                        'Failed to fetch promos. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Get promo by ID
 */
export const getPromoById = async (id: string): Promise<Promo> => {
  try {
    const endpoint = PROMOS_ENDPOINTS.PROMOS.GET_BY_ID.replace('{id}', id);
    const response = await secureApi.get(endpoint);
    
    // Transform API response
    const item = response.data?.data || response.data;
    return transformPromoResponse(item);
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || 
                        error?.response?.data?.error || 
                        error?.message || 
                        'Failed to fetch promo. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Create new promo
 */
export const createPromo = async (promoData: PromoFormData, status: PromoStatus = PromoStatus.ACTIVE): Promise<Promo> => {
  try {
    const finalPayload = buildPromoPayload(promoData, { status });
    
    const response = await secureApi.post(PROMOS_ENDPOINTS.PROMOS.CREATE, finalPayload);
    
    // Transform API response
    const item = response.data?.data || response.data;
    return transformPromoResponse(item);
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || 
                        error?.response?.data?.error || 
                        error?.message || 
                        'Failed to create promo. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Save promo as draft
 */
export const savePromoAsDraft = async (promoData: PromoFormData): Promise<Promo> => {
  return createPromo(promoData, PromoStatus.DRAFT);
};

/**
 * Update existing promo content
 * Sends create-like payload, but only changed fields if original is provided
 */
export const updatePromo = async (
  id: string,
  promoData: PromoFormData,
  originalPromoData?: PromoFormData,
): Promise<Promo> => {
  try {
    const endpoint = PROMOS_ENDPOINTS.PROMOS.UPDATE.replace('{id}', id);

    const currentPayload = buildPromoPayload(promoData);
    let payload = currentPayload;

    if (originalPromoData) {
      const originalPayload = buildPromoPayload(originalPromoData);
      const changedPayload: Record<string, any> = {};

      Object.keys(currentPayload || {}).forEach((key) => {
        if (JSON.stringify(currentPayload[key]) !== JSON.stringify(originalPayload?.[key])) {
          changedPayload[key] = currentPayload[key];
        }
      });

      payload = changedPayload;
    }
    
    const response = await secureApi.post(endpoint, payload);
    
    // Transform API response
    const item = response.data?.data || response.data;
    return transformPromoResponse(item);
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || 
                        error?.response?.data?.error || 
                        error?.message || 
                        'Failed to update promo. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Update promo status
 */
export const updatePromoStatus = async (id: string, status: PromoStatus): Promise<Promo> => {
  try {
    const endpoint = PROMOS_ENDPOINTS.PROMOS.UPDATE_STATUS.replace('{id}', id);
    const payload = {
      status: status,
    };
    
    const response = await secureApi.post(endpoint, payload);
    
    // Transform API response
    const item = response.data?.data || response.data;
    return transformPromoResponse(item);
  } catch (error: any) {
    const errorMessage = error?.response?.data?.message || 
                        error?.response?.data?.error || 
                        error?.message || 
                        'Failed to update promo status. Please try again.';
    throw new Error(errorMessage);
  }
};

/**
 * Delete promo
 */
export const deletePromo = async (id: string): Promise<void> => {
  try {
    const endpoint = PROMOS_ENDPOINTS.PROMOS.DELETE.replace('{id}', id);
    await secureApi.post(endpoint);
  } catch (error) {
    throw error;
  }
};
